import React, { useEffect, useState } from 'react';
import {
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';

const Login: React.FC = () => {
  const history = useHistory();
  const { login, user, isLoading } = useAuth();
  const { t } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!isLoading && user) {
      history.replace('/tabs/dashboard');
    }
  }, [history, isLoading, user]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!username || !password) {
      setError('Please enter both username and password.');
      setShowToast(true);
      return;
    }

    try {
      setIsSubmitting(true);
      await login(username, password);
      history.replace('/tabs/dashboard');
    } catch (error) {
      const responseMessage =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: { data?: { message?: string } } }).response?.data?.message ===
          'string'
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      const fallbackMessage =
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof (error as { message?: string }).message === 'string'
          ? (error as { message?: string }).message
          : undefined;
      const message =
        responseMessage || fallbackMessage || 'Unable to login. Please check your credentials.';
      setError(message);
      setShowToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('adminPanel')}</IonTitle>
          <LanguageToggle />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <div
          style={{
            maxWidth: 420,
            margin: '0 auto',
            paddingTop: '15vh',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <div>
            <IonText color="primary">
              <h1 style={{ fontWeight: 700, marginBottom: 8 }}>{t('login')}</h1>
            </IonText>
            <IonText color="medium">
              <p>{t('adminPanel')}</p>
            </IonText>
          </div>

          <form onSubmit={handleSubmit}>
            <IonList lines="full">
              <IonItem>
                <IonLabel position="stacked">{t('username')}</IonLabel>
                <IonInput
                  value={username}
                  placeholder={t('enterUsername')}
                  onIonChange={(event) => setUsername(event.detail.value ?? '')}
                  autocomplete="username"
                  clearOnEdit={false}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('password')}</IonLabel>
                <IonInput
                  value={password}
                  type="password"
                  placeholder={t('enterPassword')}
                  onIonChange={(event) => setPassword(event.detail.value ?? '')}
                  autocomplete="current-password"
                />
              </IonItem>
            </IonList>

            <IonButton
              expand="block"
              type="submit"
              style={{ marginTop: 24 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('loggingIn') : t('login')}
            </IonButton>
          </form>

          <IonText color="medium">
            <small>
              Cafe CN Admin Mobile
            </small>
          </IonText>
        </div>

        <IonToast
          isOpen={showToast && !!error}
          message={error || ''}
          duration={2800}
          color="danger"
          onDidDismiss={() => setShowToast(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default Login;
